import { useRef, useCallback, useState, DragEvent, ChangeEvent, useEffect } from "react";
import { preload as preloadBackgroundRemoval } from "@imgly/background-removal";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import AvatarDetails from "@/components/AvatarDetails";
import DividerWithLabel from "@/components/DividerWithLabel";
import { PhotoboothPerson } from "@/pages/photobooth/queries";
import { usePlatformDetection } from "@/hooks/usePlatformDetection";

interface UploadEntryContentProps {
  person: PhotoboothPerson;
  onCapture: (imageData: string) => void;
  onStartCamera: () => void;
}

export default function UploadEntryContent({ person, onCapture, onStartCamera }: UploadEntryContentProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { isMobile, isDesktop } = usePlatformDetection();

  useEffect(() => {
    preloadBackgroundRemoval({ model: "isnet" }).catch(() => {});
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) {
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        onCapture(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [onCapture]
  );

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      event.target.value = "";
    },
    [handleFileSelect]
  );

  const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const file = event.dataTransfer?.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const getPersonLocation = (): string => {
    const homeAddress = person.addresses?.find((addr) => addr.addressType === "HOME");
    const anyAddress = person.addresses?.[0];
    const address = homeAddress || anyAddress;

    if (address?.city && address?.state) {
      return `${address.city}, ${address.state}`;
    }
    return "";
  };

  return (
    <div className="flex flex-col gap-6" data-testid="upload-entry-content">
      <div className="bg-sys-brand-surface-container rounded-xl p-4">
        <AvatarDetails
          picture={person.profilePhoto}
          title={`${person.firstName} ${person.lastName}`}
          text={getPersonLocation()}
          secondaryText={person.pgaId ? `PGA ID: ${person.pgaId}` : ""}
        />
      </div>

      <div>
        <h2 className="text-title-small font-medium text-sys-brand-on-surface mb-4">Upload a Photo</h2>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
          data-testid="file-upload-input"
        />

        {isDesktop && (
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleUploadClick}
            className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
              ${
                isDragging
                  ? "border-sys-brand-primary bg-sys-brand-primary/5"
                  : "border-sys-brand-outline hover:border-sys-brand-primary hover:bg-sys-brand-surface-container"
              }
            `}
            data-testid="drop-zone"
          >
            <Icon
              name={isDragging ? "download" : "cloud_upload"}
              size={48}
              className={`mx-auto mb-3 ${isDragging ? "text-sys-brand-primary" : "text-sys-brand-on-surface-variant"}`}
            />
            <p className="text-body-medium text-sys-brand-on-surface mb-1">
              {isDragging ? "Drop image here" : "Drag and drop an image"}
            </p>
            <p className="text-body-small text-sys-brand-on-surface-variant">or click to browse</p>
          </div>
        )}

        {isMobile && (
          <div className="w-full">
            <Button variant="filled" onClick={handleUploadClick} icon="photo_library">
              Choose from Library
            </Button>
          </div>
        )}

        <p className="text-body-small text-sys-brand-on-surface-variant mt-3 text-center">
          Supported formats: JPEG, PNG, WebP
        </p>
      </div>

      <DividerWithLabel label="or" subtle />

      <div>
        <h2 className="text-title-small font-medium text-sys-brand-on-surface mb-4">Take a Photo</h2>
        <div className="w-full">
          <Button variant="outlined" onClick={onStartCamera} icon="photo_camera">
            Use Camera
          </Button>
        </div>
      </div>
    </div>
  );
}
